// 📂 frontend/src/App.tsx - VERSIUNEA FINALĂ COMPLETĂ
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import './styles/themes.css';
import './styles/animations.css';

import DOMPurify from 'dompurify';

// Import Theme System
import { ThemeProvider } from './contexts/ThemeContext';
import { useTheme, useThemeShortcuts } from './hooks/useTheme';

// Import componente
import ScheduleEmailForm from './components/ScheduleEmailForm';
import TemplateManager from './components/TemplateManager';
import UserManager from './components/UserManager';
import DashboardStats from './components/DashboardStats';
import SettingsPanel from './components/SettingsPanel';
import EmailList from './components/EmailList';
import BackupManager from './components/BackupManager';
import CalendarView from './components/CalendarView';
import StatsView from './components/StatsView';
import LogsViewer from './components/LogsViewer';
import NotificationBell from './components/NotificationBell';
import EmailGenerator from './components/EmailGenerator';
import ContactsView from './components/ContactsView';
import ContactGroupsView from './components/ContactGroupsView';

// Import hooks
import { useAutoRefresh } from './hooks/useAutoRefresh';
import { useToast } from './hooks/useToast';
import { ToastContainer } from './components/Toast';
import emailApi from './services/emailApi';
import ConfirmModal from './components/ConfirmModal';
import { useConfirm } from './hooks/useConfirm';

// ========================================
// 🎨 TIPURI ȘI INTERFEȚE
// ========================================

type ThemeType = 'light' | 'dark' | 'futuristic';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'admin';
  theme: ThemeType;
  createdAt: Date;
  lastLogin?: Date | null; 
  isActive: boolean;
  emailsSent: number;
  templatesCreated: number;
  contactsCount?: number;
}

interface Email {
  id: number;
  to: string;
  subject: string;
  content: string;
  scheduledFor: Date;
  status: 'scheduled' | 'sent' | 'failed';
  isRecurring?: boolean;
  reminderDays?: number;
  reminderSent?: boolean;
  originalSentDate?: Date;
  recurringType?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'none';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  emailType?: 'transactional' | 'marketing' | 'notification' | 'newsletter';
  maxOccurrences?: number;
  currentOccurrence?: number;
  endDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  userId?: number;
  sentAt?: Date;
  nextRecurrence?: Date;
  reminderSentAt?: Date;
  errorMessage?: string;
}

interface Template {
  id: number;
  name: string;
  subject: string;
  content: string;
  category: string;
  tags: string[];
  isPublic: boolean;
  createdBy: number;
  createdAt: Date;
  usageCount: number;
  variables: string[];
}

interface Settings {
  gmailEmail: string;
  gmailPassword: string;
  smtpHost: string;
  smtpPort: number;
  emailSignature: string;
  dailyLimit: number;
  timezone: string;
  autoBackup: boolean;
  darkMode: boolean;
  notifications: {
    emailSent: boolean;
    emailFailed: boolean;
    userLogin: boolean;
  };
}

// ========================================
// 🚀 COMPONENTA PRINCIPALĂ
// ========================================

const App: React.FC = () => {
  // Theme hooks
  const { theme, setTheme, currentTheme } = useTheme();
  const { handleKeyPress } = useThemeShortcuts();
  
  // Toast notifications
  const { toasts, toast, removeToast } = useToast();

  // Confirm modal
  const { confirmState, confirm, cancelConfirm } = useConfirm();

  // State pentru utilizator și autentificare
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // State pentru navigare
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // State pentru date - CONECTATE LA API
  const [scheduledEmails, setScheduledEmails] = useState<Email[]>([]);
  const [sentEmails, setSentEmails] = useState<Email[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<Settings>({
    gmailEmail: '',
    gmailPassword: '',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    emailSignature: '',
    dailyLimit: 100,
    timezone: 'Europe/Bucharest',
    autoBackup: true,
    darkMode: currentTheme === 'dark',
    notifications: {
      emailSent: true,
      emailFailed: true,
      userLogin: true
    }
  });
  
  // State pentru filtre și căutare
  const [emailFilter, setEmailFilter] = useState<'all' | 'scheduled' | 'sent'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State pentru modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [editingEmail, setEditingEmail] = useState<Email | null>(null);
  
  // Pentru login
  const [email, setEmail] = useState('admin@test.com');
  const [password, setPassword] = useState('admin123');
  const [error, setError] = useState('');

  // Loading states
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Sidebar collapsed sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // ========================================
  // 🔄 FUNCȚII API PENTRU SINCRONIZARE
  // ========================================

  // Handler global pentru erori de autentificare
  const handleAuthError = useCallback(() => {

    setUser(null);
    setScheduledEmails([]);
    setSentEmails([]);
    setTemplates([]);
    setUsers([]);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    toast.error('Sesiune expirată', 'Te rog loghează-te din nou');
  }, [toast]);

  // Încarcă email-urile de pe server
  const loadEmailsFromServer = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoadingEmails(true);

      const emailsData = await emailApi.getScheduledEmails();


      if (Array.isArray(emailsData)) {
        const scheduled: Email[] = [];
        const sent: Email[] = [];

        emailsData.forEach((emailData: Record<string, unknown>) => {
          // Convertire recipients array în string
          const rawRecipients = emailData.recipients as string | string[] | undefined;
          const recipients = Array.isArray(rawRecipients)
            ? rawRecipients.join(', ')
            : rawRecipients || '';
          
          // Creare obiect email pentru frontend
          const emailObj: Email = {
            id: emailData.id as number,
            to: recipients,
            subject: (emailData.subject as string) || '',
            content: (emailData.content as string) || (emailData.body as string) || '',
            scheduledFor: new Date(emailData.scheduled_time as string),
            status: emailData.status as 'scheduled' | 'sent' | 'failed',
            isRecurring: (emailData.is_recurring as boolean) || false,
            recurringType: (emailData.recurring_type as Email['recurringType']) || 'none',
            reminderDays: (emailData.reminder_days as number) || 0,
            priority: (emailData.priority as Email['priority']) || 'normal',
            emailType: (emailData.email_type as Email['emailType']) || 'transactional',
            userId: emailData.user_id as number,
            createdAt: emailData.created_at ? new Date(emailData.created_at as string) : new Date(),
            originalSentDate: emailData.sent_at ? new Date(emailData.sent_at as string) : undefined,
            sentAt: emailData.sent_at ? new Date(emailData.sent_at as string) : undefined,
            errorMessage: emailData.error_message as string | undefined
          };
          
          // Separare pe status
          if ((emailData.status as string) === 'sent') {
            sent.push(emailObj);
          } else if ((emailData.status as string) === 'scheduled') {
            scheduled.push(emailObj);
          }
        });
        
        // Actualizare state
        setScheduledEmails(scheduled);
        setSentEmails(sent);
        
        // Toast de confirmare
        if (scheduled.length > 0 || sent.length > 0) {
          toast.success('Sincronizare', `Încărcate: ${scheduled.length} programate, ${sent.length} trimise`);
        }
      } else {

        setScheduledEmails([]);
        setSentEmails([]);
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      toast.error('Eroare', 'Nu am putut încărca email-urile de pe server');
    } finally {
      setIsLoadingEmails(false);
    }
  }, [user, toast, handleAuthError]);

  // Încarcă template-urile de pe server
  const loadTemplatesFromServer = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoadingTemplates(true);

      const rawTemplates = await emailApi.getTemplates();

      if (Array.isArray(rawTemplates)) {
        const templatesData = rawTemplates.map((template: Record<string, unknown>) => ({
          id: template.id as number,
          name: template.name as string,
          subject: template.subject as string,
          content: (template.content as string) || (template.body as string),
          category: (template.category as string) || 'General',
          tags: template.tags ? (template.tags as string).split(',').map((t: string) => t.trim()) : [],
          isPublic: (template.is_public as boolean) || false,
          createdBy: template.user_id as number,
          createdAt: new Date(template.created_at as string),
          usageCount: (template.usage_count as number) || 0,
          variables: (template.variables as string[]) || []
        }));

        setTemplates(templatesData);

      } else {
        setTemplates([]);

      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [user, handleAuthError]);

  // Încarcă utilizatorii (doar pentru admin)
  const loadUsersFromServer = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    
    try {

      const rawUsers = await emailApi.getUsers();

      if (Array.isArray(rawUsers)) {
        const usersData = rawUsers.map((userData: Record<string, unknown>) => ({
          id: userData.id as number,
          username: userData.username as string,
          email: userData.email as string,
          role: userData.role as 'user' | 'admin',
          theme: (userData.theme_preference as ThemeType) || 'dark',
          createdAt: new Date(userData.created_at as string),
          lastLogin: userData.last_login ? new Date(userData.last_login as string) : null,
          isActive: userData.is_active as boolean,
          emailsSent: (userData.emails_sent as number) || 0,
          templatesCreated: (userData.templates_created as number) || 0
        }));

        setUsers(usersData);

      }
    } catch (error) {
      console.error('Error loading users:', error);
      if (user.role === 'admin') {
        toast.error('Eroare', 'Nu am putut încărca utilizatorii de pe server');
      }
    }
  }, [user, toast, handleAuthError]);

  // ========================================
  // 🔄 AUTO-REFRESH PENTRU EMAIL-URI
  // ========================================
  
  const refreshEmails = useCallback(async () => {
    try {
      await loadEmailsFromServer();
      
      // Verifică dacă sunt email-uri noi trimise
      const now = new Date();
      let hasNewSentEmails = false;
      
      scheduledEmails.forEach(email => {
        if (email.status === 'scheduled' && new Date(email.scheduledFor) < now) {
          hasNewSentEmails = true;
        }
      });
      
      if (hasNewSentEmails) {
        toast.info('Actualizat', 'Au fost trimise email-uri programate!');
      }
    } catch (error) {
      console.error('Error refreshing emails:', error);
    }
  }, [loadEmailsFromServer, scheduledEmails, toast]);

  const { 
    isRefreshing, 
    refresh, 
    formatTimeRemaining 
  } = useAutoRefresh({
    interval: 30000, // 30 secunde
    enabled: user !== null && activeTab === 'emails',
    onRefresh: refreshEmails
  });

  // ========================================
  // 🎨 KEYBOARD SHORTCUTS
  // ========================================
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    
    const handleAppShortcuts = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        event.preventDefault();
        if (user) {
          setActiveTab('schedule');
        }
      }
      
      if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
        event.preventDefault();
        if (user) {
          setActiveTab('emails');
        }
      }
      
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        event.preventDefault();
        if (user) {
          setActiveTab('dashboard');
        }
      }
      
      if ((event.ctrlKey || event.metaKey) && event.key === 't' && !event.shiftKey) {
        event.preventDefault();
        if (user) {
          setActiveTab('templates');
        }
      }
      
      if (event.key === 'Escape') {
        setShowEmailModal(false);
      }
    };
    
    window.addEventListener('keydown', handleAppShortcuts);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('keydown', handleAppShortcuts);
    };
  }, [handleKeyPress, user]);

  // ========================================
  // 🎨 EMAIL GENERATOR EVENT LISTENER
  // ========================================

  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState('');

  useEffect(() => {
    const handleGeneratedEmail = () => {
      const content = sessionStorage.getItem('emailGeneratorContent');
      const subject = sessionStorage.getItem('emailGeneratorSubject');
      if (content) {
        setGeneratedContent(content);
        setGeneratedSubject(subject || '');
        setActiveTab('schedule');
        sessionStorage.removeItem('emailGeneratorContent');
        sessionStorage.removeItem('emailGeneratorSubject');
      }
    };
    window.addEventListener('useGeneratedEmail', handleGeneratedEmail);
    return () => window.removeEventListener('useGeneratedEmail', handleGeneratedEmail);
  }, []);
  




  // ========================================
  // 🔐 FUNCȚII AUTENTIFICARE
  // ========================================

  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await emailApi.login(email, password);
      
      if ((response as Record<string, unknown>).success) {
        const respData = (response as Record<string, unknown>).data as Record<string, unknown>;
        const userData = respData.user as Record<string, unknown>;
        const user: User = {
          id: userData.id as number,
          username: userData.username as string,
          email: userData.email as string,
          role: userData.role as 'user' | 'admin',
          theme: (userData.theme_preference as ThemeType) || currentTheme,
          createdAt: new Date(userData.created_at as string),
          lastLogin: userData.last_login ? new Date(userData.last_login as string) : new Date(),
          isActive: userData.is_active as boolean,
          emailsSent: 0,
          templatesCreated: 0
        };
        
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
        toast.success('Autentificare Reușită', `Bun venit, ${user.username}!`);
      }
    } catch (error: unknown) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Eroare de conexiune');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setScheduledEmails([]);
    setSentEmails([]);
    setTemplates([]);
    setUsers([]);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    toast.info('Delogare', 'Te-ai delogat cu succes!');
  };

  const changeTheme = (newTheme: ThemeType) => {
    setTheme(newTheme);
    if (user) {
      const updatedUser = { ...user, theme: newTheme };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    toast.info('Tema Schimbată', `Tema a fost schimbată la ${newTheme}`);
  };

  // ========================================
  // 📧 FUNCȚII EMAIL - CONECTATE LA API
  // ========================================

  const getFilteredEmails = useMemo(() => {
    let allEmails = [...scheduledEmails, ...sentEmails];

    // Filtrare pentru utilizatori non-admin
    if (user && user.role !== 'admin') {
      allEmails = allEmails.filter(email => email.userId === user.id);
    }

    let filtered = allEmails;

    switch (emailFilter) {
      case 'scheduled':
        filtered = filtered.filter(email => email.status === 'scheduled');
        break;
      case 'sent':
        filtered = filtered.filter(email => email.status === 'sent');
        break;
      default:
        filtered = allEmails;
    }

    if (searchTerm) {
      filtered = filtered.filter(email =>
        email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered.sort((a, b) => new Date(b.scheduledFor).getTime() - new Date(a.scheduledFor).getTime());
  }, [scheduledEmails, sentEmails, user, emailFilter, searchTerm]);

  const handleViewEmail = (email: Email) => {
    setSelectedEmail(email);
    setShowEmailModal(true);
  };

  const handleEditEmail = (email: Email) => {
    if (email.status === 'sent') {
      // Pentru email-urile trimise, creează o copie nouă cu date resetate
      const newEmail = {
        ...email,
        id: Date.now(), // ID nou pentru a evita conflicte
        status: 'scheduled' as const, // Reset status
        scheduledFor: new Date(), // Data nouă (acum)
        sentAt: undefined, // Elimină data trimiterii
        errorMessage: undefined, // Elimină erori
        // Păstrează restul datelor (subject, content, to, etc.)
      };
      
      setEditingEmail(newEmail);
      toast.info('Refolosire Email', 'Poți modifica și retrimite acest email');
    } else {
      // Pentru cele programate, editează normal
      setEditingEmail(email);
    }
    
    setActiveTab('schedule');
  };

  // FUNCȚIA ACTUALIZATĂ PENTRU ȘTERGERE
  const handleDeleteEmail = async (emailId: number): Promise<void> => {
    const emailToDelete = [...scheduledEmails, ...sentEmails].find(e => e.id === emailId);
    
    if (!emailToDelete) {
      console.error('Email not found:', emailId);
      return;
    }
    
    const confirmMessage = emailToDelete.status === 'sent'
      ? 'Ești sigur că vrei să ștergi acest email din istoric?'
      : 'Ești sigur că vrei să anulezi această programare?';

    const confirmed = await confirm({
      title: 'Confirmare ștergere',
      message: confirmMessage,
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;
    
    try {
      // 1. Optimistic update - șterge imediat din UI
      if (emailToDelete.status === 'sent') {
        setSentEmails(prev => {
          return prev.filter(email => email.id !== emailId);
        });
      } else {
        setScheduledEmails(prev => {
          return prev.filter(email => email.id !== emailId);
        });
      }
      
      // 2. Apelează API pentru ștergere
      // Verifică dacă metoda există în emailApi
      if (typeof emailApi.deleteEmailPermanently === 'function') {
        // Folosește noua metodă pentru ștergere permanentă
        await emailApi.deleteEmailPermanently(emailId);
      } else {
        // Fallback pentru email-uri programate
        if (emailToDelete.status === 'scheduled') {
          await emailApi.cancelScheduledEmail(emailId);
        } else {
          // Fallback for sent emails without permanent delete
          // Pentru email-uri trimise, doar actualizează local
        }
      }
      
      // 3. Afișează mesaj de succes
      toast.success(
        emailToDelete.status === 'sent' ? 'Email Șters' : 'Programare Anulată',
        emailToDelete.status === 'sent' 
          ? 'Email-ul a fost șters din istoric!'
          : 'Programarea email-ului a fost anulată!'
      );
      
      // 4. Forțează refresh după 1 secundă pentru sincronizare completă
      setTimeout(async () => {
        await loadEmailsFromServer();
      }, 1000);
      
    } catch (error: unknown) {
      console.error('Error deleting email:', error);

      // Revert optimistic update on error
      await loadEmailsFromServer();

      toast.error('Eroare', error instanceof Error ? error.message : 'Nu am putut șterge email-ul');
    }
  };

  // Handler pentru ștergere în bulk - ACTUALIZAT
  const handleBulkDelete = async (emailIds: number[]): Promise<void> => {
    const confirmed = await confirm({
      title: 'Confirmare ștergere în masă',
      message: `Sigur vrei să ștergi ${emailIds.length} email-uri?`,
      variant: 'danger',
      confirmText: 'Șterge',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;
    
    try {
      // Optimistic update
      setScheduledEmails(prev => prev.filter(email => !emailIds.includes(email.id)));
      setSentEmails(prev => prev.filter(email => !emailIds.includes(email.id)));
      
      // Delete each email
      const deletePromises = emailIds.map(id => 
        emailApi.cancelScheduledEmail(id).catch(err => {
          console.error(`Failed to delete email ${id}:`, err);
          return null;
        })
      );
      
      const results = await Promise.all(deletePromises);
      const successCount = results.filter(r => r !== null).length;
      
      if (successCount === emailIds.length) {
        toast.success('Succes', `${successCount} email-uri șterse cu succes`);
      } else {
        toast.warning('Parțial', `${successCount} din ${emailIds.length} email-uri șterse`);
      }
      
      // Reload data
      setTimeout(() => {
        loadEmailsFromServer();
      }, 1000);
      
    } catch (error: unknown) {
      console.error('Error in bulk delete:', error);
      loadEmailsFromServer(); // Revert
      toast.error('Eroare', 'Eroare la ștergerea email-urilor');
    }
  };

  // Handler pentru ștergere totală - ACTUALIZAT
  const handleDeleteAll = async (): Promise<void> => {
    const allEmails = getFilteredEmails;
    
    if (allEmails.length === 0) {
      toast.info('Info', 'Nu există email-uri de șters');
      return;
    }
    
    const confirmed = await confirm({
      title: 'Confirmare ștergere totală',
      message: `Sigur vrei să ștergi TOATE cele ${allEmails.length} email-uri?\n\nAceastă acțiune este IREVERSIBILĂ!`,
      variant: 'danger',
      confirmText: 'Șterge tot',
      cancelText: 'Anulează'
    });
    if (!confirmed) return;
    
    // Use bulk delete
    await handleBulkDelete(allEmails.map(email => email.id));
  };

  const handleEmailSuccess = async (emailData: Record<string, unknown>) => {
    // Resetează editingEmail pentru a preveni probleme
    setEditingEmail(null);
    
    // Reîncarcă IMEDIAT datele de pe server pentru sincronizare
    try {
      // Așteaptă puțin pentru ca backend-ul să finalizeze salvarea
      setTimeout(async () => {
        await loadEmailsFromServer();
      }, 500);
      
      toast.success(
        emailData.status === 'sent' ? 'Email Trimis' : 'Email Programat',
        emailData.status === 'sent' 
          ? `Emailul către ${emailData.to} a fost trimis cu succes!`
          : `Emailul către ${emailData.to} a fost programat cu succes!`
      );
      
      setActiveTab('emails');
    } catch (error) {
      console.error('Error reloading emails:', error);
      toast.error('Eroare', 'Email-ul a fost salvat, dar nu am putut actualiza lista');
    }
  };

  // ========================================
  // 📝 FUNCȚII TEMPLATE-URI - CONECTATE LA API
  // ========================================

  const handleTemplateCreate = async (templateData: Record<string, unknown>) => {
    try {
      const response = await emailApi.createTemplate(templateData);
      
      if (response.success && response.data) {
        // Adaugă template-ul nou în listă
        const d = response.data as Record<string, unknown>;
        const newTemplate: Template = {
          id: d.id as number,
          name: d.name as string,
          subject: d.subject as string,
          content: (d.content as string) || (d.body as string),
          category: (d.category as string) || 'General',
          tags: d.tags ? (d.tags as string).split(',').map((t: string) => t.trim()) : [],
          isPublic: (d.is_public as boolean) || false,
          createdBy: d.user_id as number,
          createdAt: new Date(d.created_at as string),
          usageCount: (d.usage_count as number) || 0,
          variables: (d.variables as string[]) || []
        };
        setTemplates(prev => [newTemplate, ...prev]);
        toast.success('Template Creat', `Template-ul "${templateData.name}" a fost creat cu succes!`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Eroare', 'Nu am putut crea template-ul');
    }
  };

  const handleTemplateUpdate = async (id: number, templateData: Record<string, unknown>) => {
    try {
      await emailApi.updateTemplate(id, templateData);
      
      // Actualizează local
      setTemplates(prev => prev.map(t =>
        t.id === id ? { ...t, ...templateData } as Template : t
      ));
      toast.success('Template Actualizat', 'Template-ul a fost actualizat cu succes!');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Eroare', 'Nu am putut actualiza template-ul');
    }
  };

  const handleTemplateDelete = async (id: number) => {
    try {
      await emailApi.deleteTemplate(id);
      
      // Șterge local
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template Șters', 'Template-ul a fost șters cu succes!');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Eroare', 'Nu am putut șterge template-ul');
    }
  };

  const handleTemplateUse = async (template: Template) => {
    try {
      // Marchează template-ul ca folosit în API
      await emailApi.useTemplate(template.id);
      
      setEditingEmail({
        id: Date.now(),
        to: '',
        subject: template.subject,
        content: template.content,
        scheduledFor: new Date(),
        status: 'scheduled',
        priority: 'normal',
        emailType: 'transactional'
      });
      setActiveTab('schedule');
      toast.info('Template Selectat', `Folosești template-ul "${template.name}"`);
      
      // Actualizează usage count local
      setTemplates(prev => prev.map(t => 
        t.id === template.id ? { ...t, usageCount: t.usageCount + 1 } : t
      ));
    } catch (error) {
      console.error('Error using template:', error);
      // Continuă oricum cu template-ul
      setEditingEmail({
        id: Date.now(),
        to: '',
        subject: template.subject,
        content: template.content,
        scheduledFor: new Date(),
        status: 'scheduled',
        priority: 'normal',
        emailType: 'transactional'
      });
      setActiveTab('schedule');
      toast.info('Template Selectat', `Folosești template-ul "${template.name}"`);
    }
  };

  // ========================================
  // 👥 FUNCȚII UTILIZATORI
  // ========================================

  const handleUserCreate = async (userData: Record<string, unknown>) => {
    try {
      const response = await emailApi.createUser(userData);
      
      if (response.success && response.data) {
        const u = (response.data as Record<string, unknown>).user as Record<string, unknown>;
        const newUser: User = {
          id: u.id as number,
          username: u.username as string,
          email: u.email as string,
          role: u.role as 'user' | 'admin',
          theme: (u.theme_preference as ThemeType) || 'dark',
          createdAt: new Date(u.created_at as string),
          lastLogin: u.last_login ? new Date(u.last_login as string) : null,
          isActive: u.is_active as boolean,
          emailsSent: 0,
          templatesCreated: 0
        };
        setUsers(prev => [newUser, ...prev]);
        toast.success('Utilizator Creat', `Utilizatorul "${userData.username}" a fost creat cu succes!`);
      }
    } catch (error: unknown) {
      console.error('Error creating user:', error);
      toast.error('Eroare', error instanceof Error ? error.message : 'Nu am putut crea utilizatorul');
    }
  };

  const handleUserUpdate = async (id: number, userData: Record<string, unknown>) => {
    try {
      const response = await emailApi.updateUser(id, userData);
      
      if (response.success) {
        setUsers(prev => prev.map(u => 
          u.id === id ? { ...u, ...userData } : u
        ));
        toast.success('Utilizator Actualizat', 'Datele utilizatorului au fost actualizate!');
      }
    } catch (error: unknown) {
      console.error('Error updating user:', error);
      toast.error('Eroare', error instanceof Error ? error.message : 'Nu am putut actualiza utilizatorul');
    }
  };

  const handleUserDelete = async (id: number) => {
    try {
      await emailApi.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success('Utilizator Șters', 'Utilizatorul a fost șters cu succes!');
    } catch (error: unknown) {
      console.error('Error deleting user:', error);
      toast.error('Eroare', error instanceof Error ? error.message : 'Nu am putut șterge utilizatorul');
    }
  };

  const handleUserToggle = async (id: number) => {
    try {
      const response = await emailApi.toggleUserStatus(id);

      if (response.success) {
        setUsers(prev => prev.map(u =>
          u.id === id ? { ...u, isActive: !u.isActive } : u
        ));
        const user = users.find(u => u.id === id);
        toast.info('Status Actualizat', `Utilizatorul ${user?.username} a fost ${user?.isActive ? 'dezactivat' : 'activat'}`);
      }
    } catch (error: unknown) {
      console.error('Error toggling user:', error);
      toast.error('Eroare', error instanceof Error ? error.message : 'Nu am putut actualiza statusul');
    }
  };

  // ========================================
  // ⚙️ FUNCȚII SETĂRI
  // ========================================

  const handleSettingsUpdate = (newSettings: Settings) => {
    setSettings(newSettings);
    localStorage.setItem('app-settings', JSON.stringify(newSettings));
    toast.success('Setări Salvate', 'Setările au fost salvate cu succes!');
  };

  // ========================================
  // 🔄 EFECTE
  // ========================================

  // Load saved user and settings on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
    }

    const savedSettings = localStorage.getItem('app-settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Load data when user changes
  useEffect(() => {
    if (user) {
      loadEmailsFromServer();
      loadTemplatesFromServer();
      if (user.role === 'admin') {
        loadUsersFromServer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only reload when user ID changes

  // Handle auth errors globally
  useEffect(() => {
    const handleAuthEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.status === 401) {
        handleAuthError();
      }
    };

    window.addEventListener('auth-error', handleAuthEvent);

    return () => {
      window.removeEventListener('auth-error', handleAuthEvent);
    };
  }, [handleAuthError]);

  // ========================================
  // 🎯 COMPONENTE UI
  // ========================================

  const ThemeSelector = () => (
    <div className="theme-selector">
      <span style={{ color: theme.colors.textSecondary, fontSize: '14px', marginRight: '8px' }}>
        Tema:
      </span>
      <button
        onClick={() => changeTheme('light')}
        className={`theme-btn ${currentTheme === 'light' ? 'active' : ''}`}
        title="Light Theme"
      >
        ☀️
      </button>
      <button
        onClick={() => changeTheme('dark')}
        className={`theme-btn ${currentTheme === 'dark' ? 'active' : ''}`}
        title="Dark Theme"
      >
        🌙
      </button>
      <button
        onClick={() => changeTheme('futuristic')}
        className={`theme-btn ${currentTheme === 'futuristic' ? 'active' : ''}`}
        title="Futuristic Theme"
      >
        ⚡
      </button>
    </div>
  );

  // ========================================
  // 🎨 STILURI CSS-IN-JS CU TEMA
  // ========================================

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      transition: theme.transitions.slow
    },
    loginContainer: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: currentTheme === 'futuristic' 
        ? `radial-gradient(circle at 50% 50%, ${theme.colors.primary}22 0%, ${theme.colors.background} 50%)`
        : theme.colors.background
    },
    loginBox: {
      width: '100%',
      maxWidth: '400px',
      padding: '40px',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.large,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.large,
      ...(currentTheme === 'futuristic' && {
        position: 'relative' as const,
        overflow: 'hidden' as const
      })
    },
    input: {
      width: '100%',
      padding: '12px',
      margin: '8px 0',
      borderRadius: theme.borderRadius.medium,
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      fontSize: '16px',
      boxSizing: 'border-box' as const,
      transition: theme.transitions.fast
    },
    button: {
      padding: '12px 24px',
      backgroundColor: theme.colors.primary,
      color: '#ffffff',
      border: 'none',
      borderRadius: theme.borderRadius.medium,
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      transition: theme.transitions.normal,
      ...(currentTheme === 'futuristic' && {
        textTransform: 'uppercase' as const,
        letterSpacing: '0.1em'
      })
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      backgroundColor: theme.colors.surface,
      borderBottom: `1px solid ${theme.colors.border}`,
      transition: theme.transitions.slow
    },
    sidebar: {
      width: '250px',
      backgroundColor: theme.colors.surface,
      borderRight: `1px solid ${theme.colors.border}`,
      minHeight: 'calc(100vh - 80px)',
      padding: '20px',
      transition: theme.transitions.slow
    },
    menuItem: {
      display: 'block',
      width: '100%',
      padding: '12px 16px',
      margin: '4px 0',
      backgroundColor: 'transparent',
      color: theme.colors.text,
      border: 'none',
      borderRadius: theme.borderRadius.medium,
      cursor: 'pointer',
      textAlign: 'left' as const,
      transition: theme.transitions.fast
    },
    main: {
      flex: 1,
      padding: '30px',
      ...(currentTheme === 'futuristic' && {
        position: 'relative' as const
      })
    },
    card: {
      backgroundColor: theme.colors.surface,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.large,
      padding: '20px',
      marginBottom: '20px',
      transition: theme.transitions.normal,
      position: 'relative' as const
    }
  };

  // ========================================
  // 🔐 PAGINA DE LOGIN
  // ========================================

  if (!user) {
    return (
      <div style={styles.container}>
        <ToastContainer toasts={toasts} onClose={removeToast} />
        
        <div style={styles.loginContainer}>
          <div style={styles.loginBox} className={currentTheme === 'futuristic' ? 'futuristic-border' : ''}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                ...(currentTheme === 'futuristic' && { letterSpacing: '0.1em' })
              }} className={currentTheme === 'futuristic' ? 'holographic' : ''}>
                📧 Email Automation
              </h1>
              <p style={{ color: theme.colors.textSecondary }}>
                Conectează-te la contul tău
              </p>
            </div>

            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <ThemeSelector />
            </div>

            {error && (
              <div style={{
                padding: '12px',
                marginBottom: '20px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: theme.borderRadius.medium,
                color: theme.colors.danger
              }}>
                {error}
              </div>
            )}

            <form onSubmit={login}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Email
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@test.com"
                  style={styles.input}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Parolă
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="admin123"
                  style={styles.input}
                  required
                />
              </div>

              <button 
                type="submit" 
                disabled={isLoading}
                style={{
                  ...styles.button,
                  width: '100%',
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
                className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
              >
                {isLoading ? 'Se conectează...' : 'Conectare'}
              </button>
            </form>

            <div style={{ 
              marginTop: '30px', 
              paddingTop: '20px', 
              borderTop: `1px solid ${theme.colors.border}`,
              fontSize: '12px',
              color: theme.colors.textSecondary 
            }}>
              <p><strong>🧪 Demo accounts:</strong></p>
              <p>Admin: admin@test.com / admin123</p>
              <p>User: user1@test.com / test123</p>
              <p style={{ marginTop: '10px' }}><strong>⌨️ Shortcuts:</strong></p>
              <p>Ctrl+Shift+T - Schimbă tema</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // 📊 DASHBOARD PRINCIPAL
  // ========================================

  const menuSections = [
    {
      id: 'principal',
      label: '📊 Principal',
      items: [
        { id: 'dashboard', label: '📊 Dashboard', admin: false },
        { id: 'emails', label: '📬 Email-uri', admin: false },
      ]
    },
    {
      id: 'compune',
      label: '📧 Compune',
      items: [
        { id: 'schedule', label: '📅 Programează', admin: false },
        { id: 'generator', label: '🎨 Generator', admin: false },
      ]
    },
    {
      id: 'contacte',
      label: '📇 Contacte',
      items: [
        { id: 'contacts', label: '📇 Agenda', admin: false },
        { id: 'groups', label: '👥 Grupuri', admin: false },
      ]
    },
    {
      id: 'administrare',
      label: '📋 Administrare',
      items: [
        { id: 'templates', label: '✉️ Template-uri', admin: false },
        { id: 'calendar', label: '📅 Calendar', admin: false },
        { id: 'stats', label: '📊 Statistici', admin: false },
        { id: 'settings', label: '⚙️ Setări', admin: true },
        { id: 'backup', label: '💾 Backup', admin: true },
        { id: 'logs', label: '📋 Loguri', admin: true },
        { id: 'users', label: '👥 Utilizatori', admin: true },
      ]
    }
  ];

  return (
    <div style={styles.container}>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      <div className="header-bar" style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: 'bold',
            ...(currentTheme === 'futuristic' && { letterSpacing: '0.1em' })
          }} className={currentTheme === 'futuristic' ? 'effect-glow' : ''}>
            📧 Email Automation
          </h1>
          {user?.role === 'admin' && (
            <span style={{
              padding: '4px 8px',
              backgroundColor: theme.colors.accent + '33',
              color: theme.colors.accent,
              fontSize: '12px',
              fontWeight: 'bold',
              borderRadius: theme.borderRadius.small
            }}>
              ADMIN
            </span>
          )}
        </div>

        <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <NotificationBell />
          <ThemeSelector />
          <span style={{ color: theme.colors.textSecondary }}>
            {user?.username}
          </span>
          <button
            onClick={logout}
            style={styles.button}
            className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      <div className="app-layout" style={{ display: 'flex' }}>
        <div className="sidebar" style={styles.sidebar}>
          {menuSections.map(section => {
            const visibleItems = section.items.filter(item => !item.admin || user?.role === 'admin');
            if (visibleItems.length === 0) return null;
            const isCollapsed = collapsedSections.has(section.id);

            return (
              <div key={section.id} className="sidebar-section" style={{ marginBottom: '8px' }}>
                <button
                  className="sidebar-section-header"
                  onClick={() => {
                    setCollapsedSections(prev => {
                      const next = new Set(prev);
                      if (next.has(section.id)) next.delete(section.id);
                      else next.add(section.id);
                      return next;
                    });
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    color: theme.colors.textSecondary,
                    border: 'none',
                    borderRadius: theme.borderRadius.small,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    textAlign: 'left'
                  }}
                >
                  <span>{section.label}</span>
                  <span style={{ fontSize: '10px', transition: 'transform 0.2s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                    ▼
                  </span>
                </button>
                {!isCollapsed && visibleItems.map(item => (
                  <button
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id && currentTheme === 'futuristic' ? 'effect-glow' : ''}`}
                    onClick={() => setActiveTab(item.id)}
                    style={{
                      ...styles.menuItem,
                      paddingLeft: '24px',
                      backgroundColor: activeTab === item.id ? theme.colors.primary : 'transparent',
                      color: activeTab === item.id ? '#ffffff' : theme.colors.text
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            );
          })}

          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: theme.colors.background,
            borderRadius: theme.borderRadius.medium,
            fontSize: '12px',
            color: theme.colors.textSecondary
          }}>
            <p><strong>⌨️ Shortcuts:</strong></p>
            <p>Ctrl+N - Email nou</p>
            <p>Ctrl+E - Email-uri</p>
            <p>Ctrl+D - Dashboard</p>
            <p>Ctrl+T - Template-uri</p>
            <p>Ctrl+Shift+T - Teme</p>
          </div>
        </div>

        <div style={styles.main} className={`main-content ${currentTheme === 'futuristic' ? 'grid-bg' : ''}`}>
          <div style={styles.card} className="card-hover theme-transition">
            {activeTab === 'dashboard' && (
              <DashboardStats
                scheduledEmails={scheduledEmails}
                sentEmails={sentEmails}
                templates={templates}
                users={users}
                theme={theme}
                currentTheme={currentTheme}
              />
            )}

            {activeTab === 'emails' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 5px 0' }} className={currentTheme === 'futuristic' ? 'effect-glow' : ''}>
                      📬 Email-uri {isLoadingEmails && <span style={{ fontSize: '14px' }}>🔄</span>}
                    </h2>
                    <p style={{ margin: '0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                      Total: {getFilteredEmails.length} • 
                      Programate: {getFilteredEmails.filter(e => e.status === 'scheduled').length} • 
                      Trimise: {getFilteredEmails.filter(e => e.status === 'sent').length}
                      {getFilteredEmails.filter(e => e.isRecurring).length > 0 && 
                        ` • Recurente: ${getFilteredEmails.filter(e => e.isRecurring).length}`
                      }
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    {/* Auto-refresh indicator */}
                    <div 
                      className={`refresh-indicator ${isRefreshing ? 'refreshing' : ''}`}
                      onClick={() => refresh()}
                      style={{ cursor: 'pointer' }}
                      title="Click pentru refresh manual"
                    >
                      <span className={isRefreshing ? 'refresh-spinner' : ''}>🔄</span>
                      <span>{isRefreshing ? 'Se actualizează...' : `Refresh în ${formatTimeRemaining()}`}</span>
                    </div>
                    
                    <button
                      onClick={() => setActiveTab('schedule')}
                      style={styles.button}
                      className={currentTheme === 'futuristic' ? 'btn futuristic-btn' : ''}
                    >
                      ➕ Email Nou
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  {[
                    { key: 'all', label: '📂 Toate', count: getFilteredEmails.length },
                    { key: 'scheduled', label: '⏳ Programate', count: getFilteredEmails.filter(e => e.status === 'scheduled').length },
                    { key: 'sent', label: '✅ Trimise', count: getFilteredEmails.filter(e => e.status === 'sent').length }
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setEmailFilter(filter.key as 'all' | 'scheduled' | 'sent')}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: emailFilter === filter.key ? theme.colors.primary : 'transparent',
                        color: emailFilter === filter.key ? '#ffffff' : theme.colors.text,
                        border: `1px solid ${emailFilter === filter.key ? theme.colors.primary : theme.colors.border}`,
                        borderRadius: theme.borderRadius.small,
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: theme.transitions.fast
                      }}
                    >
                      {filter.label} ({filter.count})
                    </button>
                  ))}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Caută email-uri..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.input}
                  />
                </div>
                
                {/* Folosește componenta EmailList cu handlers actualizați */}
                <EmailList
                  emails={getFilteredEmails}
                  onEmailView={handleViewEmail}
                  onEmailEdit={handleEditEmail}
                  onEmailDelete={handleDeleteEmail}
                  onBulkDelete={handleBulkDelete}
                  onDeleteAll={handleDeleteAll}
                  onRefresh={loadEmailsFromServer}
                  isLoading={isLoadingEmails}
                />
              </div>
            )}

            {activeTab === 'schedule' && (
              <div>
                <ScheduleEmailForm
                  theme={theme.colors}
                  onSuccess={(data) => { setGeneratedContent(''); setGeneratedSubject(''); handleEmailSuccess(data); }}
                  editingEmail={editingEmail}
                  prefillContent={generatedContent}
                  prefillSubject={generatedSubject}
                  onCancel={() => {
                    setEditingEmail(null);
                    setActiveTab('emails');
                  }}
                  onTemplateCreated={() => {
                    // Reîncarcă template-urile când se creează unul nou
                    loadTemplatesFromServer();
                    // Opțional: navighează la template-uri după 2 secunde
                    setTimeout(() => {
                      setActiveTab('templates');
                    }, 2000);
                  }}
                />
              </div>
            )}

            {activeTab === 'templates' && (
              <div>
                {isLoadingTemplates ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
                    <h3>Se încarcă template-urile...</h3>
                  </div>
                ) : (
                  <TemplateManager
                    templates={templates}
                    onTemplateCreate={handleTemplateCreate}
                    onTemplateUpdate={handleTemplateUpdate}
                    onTemplateDelete={handleTemplateDelete}
                    onTemplateUse={handleTemplateUse}
                    currentUserId={user?.id || 1}
                    isAdmin={user?.role === 'admin'}
                  />
                )}
              </div>
            )}

            {activeTab === 'generator' && (
              <EmailGenerator />
            )}

            {activeTab === 'contacts' && (
              <ContactsView />
            )}

            {activeTab === 'groups' && (
              <ContactGroupsView />
            )}

            {activeTab === 'calendar' && (
              <CalendarView />
            )}

            {activeTab === 'stats' && (
              <StatsView />
            )}

            {activeTab === 'logs' && user?.role === 'admin' && (
              <LogsViewer />
            )}

            {activeTab === 'users' && user?.role === 'admin' && (
              <UserManager
                users={users}
                currentUserId={user.id}
                onUserCreate={handleUserCreate}
                onUserUpdate={handleUserUpdate}
                onUserDelete={handleUserDelete}
                onUserToggle={handleUserToggle}
                theme={theme}
                currentTheme={currentTheme}
              />
            )}

            {activeTab === 'backup' && user?.role === 'admin' && (
              <BackupManager
                theme={theme}
                currentTheme={currentTheme}
              />
            )}

            {activeTab === 'settings' && user?.role === 'admin' && (
              <SettingsPanel
                settings={settings}
                onSettingsUpdate={handleSettingsUpdate}
                theme={theme}
                currentTheme={currentTheme}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal pentru vizualizare email */}
      {showEmailModal && selectedEmail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={() => setShowEmailModal(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Detalii email"
            style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.large,
            padding: '30px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '20px' }}>📧 Detalii Email</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Către:</strong> {selectedEmail.to}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Subiect:</strong> {selectedEmail.subject}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Status:</strong> {selectedEmail.status === 'sent' ? '✅ Trimis' : '⏳ Programat'}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Data:</strong> {selectedEmail.scheduledFor.toLocaleDateString('ro-RO')} la{' '}
              {selectedEmail.scheduledFor.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
            </div>

            {selectedEmail.isRecurring && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Recurent:</strong> {selectedEmail.recurringType}
              </div>
            )}

            {selectedEmail.reminderDays && selectedEmail.reminderDays > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <strong>Reminder:</strong> {selectedEmail.reminderDays} zile înainte
              </div>
            )}
            
            <div style={{
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.medium,
              padding: '20px',
              marginBottom: '20px'
            }}>
              <strong>Conținut:</strong>
              {selectedEmail.content.includes('<') && selectedEmail.content.includes('>') ? (
                <iframe
                  srcDoc={selectedEmail.content}
                  style={{
                    width: '100%',
                    height: '400px',
                    border: 'none',
                    borderRadius: theme.borderRadius.small,
                    marginTop: '10px',
                    backgroundColor: '#ffffff'
                  }}
                  sandbox="allow-same-origin"
                  title="Email Preview"
                />
              ) : (
                <pre style={{
                  marginTop: '10px',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit'
                }}>
                  {selectedEmail.content}
                </pre>
              )}
            </div>
            
            <button
              onClick={() => setShowEmailModal(false)}
              style={styles.button}
            >
              Închide
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        onConfirm={confirmState.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
};

// ========================================
// 🎨 APP CU THEME PROVIDER
// ========================================

const AppWithTheme = () => (
  <ThemeProvider>
    <App />
  </ThemeProvider>
);

export default AppWithTheme;